<template>
  <v-layout>
    <v-app>
      <v-app-bar border="b" flat height="56">
        <div class="d-flex align-center overflow-x-auto px-2" style="max-width: calc(100% - 68px);">
          <v-btn
            v-for="item in menuItems"
            :key="item.name"
            :to="{ name: item.name }"
            class="text-none"
            :icon="$vuetify.display.xsOnly"
            :active="$route.name === item.name"
            :min-width="$vuetify.display.xsOnly ? 40 : undefined"
            density="compact"
          >
            <v-icon size="20">{{ item.icon }}</v-icon>
            <span 
              v-if="$vuetify.display.smAndUp" 
              class="ms-1 text-body-2"
            >
              {{ $t(item.text) }}
            </span>
          </v-btn>
        </div>

        <v-spacer></v-spacer>

        <v-btn 
          icon 
          density="compact"
          class="ms-auto"
          style="min-width: 40px;"
        >
          <v-menu location="bottom end" :close-on-content-click="true">
            <template v-slot:activator="{ props }">
              <v-btn icon v-bind="props" density="compact">
                <v-icon size="20">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="toggleTheme">
                <template v-slot:prepend>
                  <v-icon>mdi-theme-light-dark</v-icon>
                </template>
                <v-list-item-title>
                  {{ $t('switchTheme', { theme: $vuetify.theme.global.current.dark ? 'Light' : 'Dark' }) }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="toggleFullscreen">
                <template v-slot:prepend>
                  <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                </template>
                <v-list-item-title>
                  {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="state.showDuckDBDialog = true">
                <template v-slot:prepend>
                  <v-icon>mdi-duck</v-icon>
                </template>
                <v-list-item-title>
                  DuckDB Query
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-btn>
      </v-app-bar>

      <v-dialog v-model="state.showWarningDialog" persistent max-width="400">
        <v-card>
          <v-card-title class="text-h5">
            Missing Parameters
          </v-card-title>
          <v-card-text>
            The following required parameters are missing from the URL:
            <ul class="mt-2">
              <li v-for="param in state.missingParams" :key="param" class="mt-1">
                {{ param }}
              </li>
            </ul>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" @click="handleMissingParams">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- DuckDB Query Dialog -->
      <DuckDBQueryDialog v-model="state.showDuckDBDialog" />

      <v-main>
        <div class="pa-4 content-container">
          <router-view
            @itemsUpdated="handleItemsUpdated"
          ></router-view>

          <WelcomeDialog
            :api-progress="apiProgress"
            :total-api-requests="totalApiRequests"
          />
        </div>
      </v-main>
    </v-app>
  </v-layout>
</template>

<script setup>
import { reactive, computed, watch, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { proxyUrl } from '/config';
import WelcomeDialog from './components/dialogs/WelcomeDialog.vue';
import DuckDBQueryDialog from './components/dialogs/DuckDBQueryDialog.vue';

const store = useStore();
const { t } = useI18n();
const vuetifyTheme = useTheme();
const currentTheme = computed(() => store.state.theme);

const getUserColorScheme = () => {
  if (window.matchMedia) { 
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      vuetifyTheme.global.name.value = 'dark'; //set immediately 
      return 'dark';
    } else {
      return 'light';
    }
    
  } else {
    return currentTheme.value;
  }
};

const theme = ref(getUserColorScheme());
store.commit('setTheme', theme.value);

const state = reactive({
  open: [],
  headers: [
    { title: "Id", key: "clientId", sortable: true },
    { title: "Assigned", value: "itemTitle", sortable: true },
    { title: "Drawing", value: "itemDrawing", sortable: true },
    { title: "Location", value: "itemLocation", sortable: true },
    { title: "Updated Date", value: "itemUpdated", sortable: true },
    { title: "Status", value: "itemStatus", sortable: true }
  ],
  filters: {},
  showWarningDialog: false,
  missingParams: [],
  showDuckDBDialog: false,
});

const menuItems = computed(() => [
  { name: 'Project', icon: 'mdi-home-outline', text: t('project') },
  { name: 'Tasks', icon: 'mdi-calendar-check', text: t('tasks') },
  { name: 'Teams', icon: 'mdi-account-multiple-outline', text: t('teams') },
  { name: 'Drawings', icon: 'mdi-map', text: t('drawings') },
  { name: 'Documents', icon: 'mdi-file-document-outline', text: t('documents') },
  { name: 'Controllers', icon: 'mdi-account-check-outline', text: t('controllers') },
  { name: 'Activity', icon: 'mdi-poll', text: t('activity') }
]);

const settings = computed(() => store.state.settings);
const loadDashboard = computed(() => store.getters.getLoadDashboard);

const buildMode = ref('production');
const isFullscreen = ref(false);

const apiProgress = ref(0); 
const totalApiRequests = 11; 

const requiredParams = [
  'scopedToken',
  'projectName',
  'projectCode',
  'locale',
  'apiUrl'
];
const parseQuerystring = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const params = {
      scopedToken: urlParams.get('scopedToken'),
      projectName: urlParams.get('projectName'),
      projectCode: urlParams.get('projectCode'),
      locale: urlParams.get('locale'),
      apiUrl: urlParams.get('apiUrl')
  };

  // Validate parameters
  state.missingParams = requiredParams.filter(param => !params[param]);

  if (state.missingParams.length > 0) {
    state.showWarningDialog = true;
    console.error('Missing required parameters:', state.missingParams);
    return false;
  }

  // If all parameters are present, store them
  store.commit('setValues', {
    ...params
  });

  // Split the token into parts
  const [headerB64, payloadB64, signature] = params.scopedToken.split('.');

  // Decode the payload
  const payloadJson = base64UrlDecode(payloadB64);
  const payload = JSON.parse(payloadJson);
  console.log("User ID:", payload.rdrive_user_id);
  await store.dispatch('updateUserId', payload.rdrive_user_id);

  return true;
};

const handleMissingParams = () => {
  state.showWarningDialog = false;
  window.close();
};

function base64UrlDecode(input) {
  // Replace URL-safe characters and add padding if needed
  input = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = input.length % 4;
  if (pad) {
    input += '='.repeat(4 - pad);
  }
  
  // Decode the base64 string
  return atob(input);
}

const getData = async () => {
  try {

    if (!store.getters.isDuckDBInitialized) {
      console.error('DuckDB not initialized yet. Please wait for initialization to complete.');
      return;
    }

    apiProgress.value = 0;
    store.commit('setDataLoaded', false);

    const duckDBService = store.getters.getDuckDBService;
    const conn = store.getters.getDBConnection;

    if (!conn) {
      throw new Error('DuckDB connection not initialized');
    }

    const apiRequests = [
      { path: `/api/${settings.value.projectCode}/reporting/sub-items/latest/`, table: 'items', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/schedules`, table: 'schedules', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/drill-downs`, table: 'drilldowns', responseType: 'parquet' },
      { path: `/api/schedule-templates`, table: 'schedule_templates', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/reporting/project-item-templates`, table: 'item_templates', responseType: 'parquet' },
      { path: '/api/categories', table: 'categories', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/reporting/items/item-status-templates`, table: 'status_templates', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/drawings`, table: 'drawings', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/locations`, table: 'locations', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/teams`, table: 'teams', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/bi/teams`, table: 'team_users', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/users`, table: 'users', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/documents`, table: 'documents', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/process-profiles`, table: 'process_profiles', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/issue-profiles`, table: 'issue_profiles', responseType: 'parquet' },
      { path: '/api/users/current', table: 'current_user', responseType: 'json' },
      { path: `/api/${settings.value.projectCode}/bi/form-templates`, table: 'form_templates', responseType: 'parquet' },
      { path: `/api/${settings.value.projectCode}/users/current/permission-rules`, table: 'permission_rules', responseType: 'json' },
      { path: '/api/company', table: 'company', responseType: 'json' },
    ];

    const results = await Promise.allSettled(
      apiRequests.map(async ({ path, table, responseType, requiresPaging = false }) => {
        switch (responseType) {
          case 'parquet':
            if (requiresPaging) {
              await duckDBService.downloadAndInsertParquetWithPaging(path, table);
            } else {
              await duckDBService.downloadAndInsertParquet(path, table);
            }
            break;
          case 'arrow':
            await duckDBService.downloadAndInsertArrow(path, table);
            break;
          default: // JSON or any other type
            await duckDBService.downloadAndInsertJSON(path, table);
            break;
        }
        
        apiProgress.value += 1;
        return { table };
      })
    );

    //placeholder, no data loaded yet.
      await conn.loadData(`
        CREATE TABLE status_history_temporal(
            clientId VARCHAR,
            id VARCHAR,
            statusId VARCHAR,
            templateId VARCHAR,
            createdByTeamId VARCHAR,
            createdByUserId VARCHAR,
            itemId VARCHAR,
            subItemId VARCHAR,
            "type" VARCHAR,
            validFrom TIMESTAMP NOT NULL,
            validTo TIMESTAMP,
            PRIMARY KEY(id, validFrom)
        );
      `);

      await conn.loadData(`
        UPDATE status_templates
        SET sort = 
            CASE 
                WHEN isTerminal AND NOT isPassed THEN 50
                ELSE sort + 
                    CASE WHEN isDraft THEN -100 ELSE 0 END +
                    CASE WHEN isInitial THEN -50 ELSE 0 END +
                    CASE WHEN isTerminal THEN 100 ELSE 0 END
            END;
      `);

    const failedResults = results.filter(result => result.status === 'rejected');
    if (failedResults.length > 0) {
      console.error('Some API requests failed:', failedResults);
    }

    store.commit('setDataLoaded', true);
    await store.state.filterItemsContext.initializeFilterContext();
    await store.state.filterDocumentsContext.initializeFilterContext();
    

    store.commit('setShowWelcomeDialog', false);
    store.commit('setLoadDashboard', false);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  store.commit('setTheme', theme.value);
  vuetifyTheme.global.name.value = theme.value;
};

const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
colorSchemeQuery.addEventListener('change', (e) => {  
  theme.value = e.matches ? 'dark' : 'light';
  vuetifyTheme.global.name.value = theme.value;
});

watch(
  () => [
    settings.value.scopedToken, 
    settings.value.projectCode,
    loadDashboard.value
  ],
  ([newScopedToken, newProjectCode, newLoadDashboard]) => {
    if (newScopedToken && newProjectCode && newLoadDashboard) {
      getData();
    }
  }
);


const updateTheme = (e) => {
  theme.value = e.matches ? 'dark' : 'light';
  store.commit('setTheme', theme.value);
};

const toggleFullscreen = async () => {
  try {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if (document.documentElement.webkitRequestFullscreen) { // Safari
        await document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) { // IE11
        await document.documentElement.msRequestFullscreen();
      }
      isFullscreen.value = true;
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) { // Safari
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) { // IE11
        await document.msExitFullscreen();
      }
      isFullscreen.value = false;
    }
  } catch (err) {
    console.error('Error toggling fullscreen:', err);
  }
};

const handleItemsUpdated = () => {
  // Handle items updated event
  //console.log('Items updated event received');
};

onMounted(async () => {
  buildMode.value = 'webapp';

  const queryParamsValid = parseQuerystring(); 
  if (!queryParamsValid) {
    return; // Stop further initialization if parameters are invalid
  }

  const proxyUrl = 'integration-proxy.rdrive.io'; // Replace with actual proxy URL
  const { apiUrl, scopedToken } = store.state.settings;

  // Initialize DuckDB with proxyUrl, apiUrl, and scopedToken
  await store.dispatch('initializeDuckDB', { proxyUrl, apiUrl, scopedToken });

  store.dispatch('updateBuildMode', buildMode.value);
  store.commit('setTheme', theme.value);
  colorSchemeQuery.addEventListener('change', updateTheme);

  // Add fullscreen change event listener
  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement;
  });
  document.addEventListener('webkitfullscreenchange', () => {
    isFullscreen.value = !!document.webkitFullscreenElement;
  });
  document.addEventListener('mozfullscreenchange', () => {
    isFullscreen.value = !!document.mozFullscreenElement;
  });
  document.addEventListener('MSFullscreenChange', () => {
    isFullscreen.value = !!document.msFullscreenElement;
  });
});

onUnmounted(() => {
  colorSchemeQuery.removeEventListener('change', updateTheme);

  // Remove fullscreen change event listeners
  document.removeEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement;
  });
  document.removeEventListener('webkitfullscreenchange', () => {
    isFullscreen.value = !!document.webkitFullscreenElement;
  });
  document.removeEventListener('mozfullscreenchange', () => {
    isFullscreen.value = !!document.mozFullscreenElement;
  });
  document.removeEventListener('MSFullscreenChange', () => {
    isFullscreen.value = !!document.msFullscreenElement;
  });
});
</script>

<style>
.content-container {
  padding: 16px;
  overflow-x: auto;
}

.fab-container {
  position: fixed;
  bottom: 16px;
  right: 16px;
}

.logo-btn {
  padding: 0 0 0 10px!important;
  min-width: 36px !important;
  width: 80px !important;
}

.logo-img {
  max-width: 80px;
  object-fit: contain;
}

.value-text {
  color: black;
}

.title-text {
  color: white;
}

.v-card {
  margin: 0 auto;
}

.margin-bottom {
  margin-bottom: 16px;
}

.v-chip {
  padding: 0 4px !important;
}

.custom-drawer {
  /* Base styles for all list items */
  .v-list-item {
    padding-left: 8px !important;
  }

  .v-list-group__items .v-list-item {
    padding-inline-start: calc(-16px + var(--indent-padding)) !important;
  }

  /* First level items */
  > .v-list > .v-list-item,
  > .v-list > .v-list-group > .v-list-group__activator > .v-list-item {
    padding-left: 0px !important;
  }

  /* Second level items */
  > .v-list > .v-list-group > .v-list-group__items > .v-list-item,
  > .v-list > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__activator > .v-list-item {
    padding-left: 0px !important;
  }

  /* Third level items */
  > .v-list > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__items > .v-list-item,
  > .v-list > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__activator > .v-list-item {
    padding-left: 0px !important;
    text-align: right !important;
    padding-right: 0px !important;
  }

  /* Third level item content alignment */
  > .v-list > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__items > .v-list-item > .v-list-item__content,
  > .v-list > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__items > .v-list-group > .v-list-group__activator > .v-list-item > .v-list-item__content {
    display: flex;
    justify-content: flex-end;
  }

  /* Specific fix for Settings > Project and similar items */
  > .v-list > .v-list-group[value="settings"] > .v-list-group__items > .v-list-item:not(.v-list-group__activator) {
    padding-left: 10px !important;
  }
}
</style>